<template>
  <div
    class="vp-input-box"
    :class="{ dirty }"
  >
    <Field
      v-bind="props"
      ref="fieldRef"
      v-slot="{ uid }"
    >
      <VPLabel
        v-if="label"
        :for="uid"
      >
        {{ label }}
      </VPLabel>
      <Accessible v-if="accessibleLabel">
        <label :for="uid">{{ accessibleLabel }}</label>
      </Accessible>
      <input
        v-bind="$attrs"
        :id="uid"
        ref="inputRef"
        :value="displayValue"
        :placeholder="placeholder"
        class="vp-input vp-price-input"
        @keydown="onKeyDown"
        @blur="onBlur"
      >
    </Field>
  </div>
</template>

<script setup lang="ts">
import { Field } from 'vuniform';
import { computed, ref, inject } from 'vue';
import { formatCurrency } from '@/helpers/currency';
import { useFormsStore } from 'vuniform';
import VPLabel from '@/components/form/VPLabel.vue';
import Accessible from '@/components/Accessible.vue';

const formsStore = useFormsStore();

const formId = inject('formId', '');

interface Props {
  inputId: string;
  placeholder?: string;
  type?: string;
  label?: string;
  accessibleLabel?: string;
  errors?: string[];
  forceError?: boolean;
  overrideValue?: string;
  hideEmptyInput?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  errors: () => [],
  placeholder: ' ',
});

const emit = defineEmits<{
  (e: 'blur'): void;
}>();

const fieldRef = ref<Field>(null);
const inputRef = ref(null);

const dirty = computed(() => formsStore.isFieldDirty(formId, props.inputId));

const onBlur = (e: Event) => {
  const priceStrRaw: string = (<HTMLInputElement>e.target).value;
  const priceStr = priceStrRaw.replace(/[^0-9.]/g, '');

  if (fieldRef.value?.value == 0 || fieldRef.value?.value || priceStr !== '') {
    let p;
    if (priceStr.length) {
      p = Number.parseFloat(priceStr);
      if (isNaN(p)) {
        p = 0.0;
      }
    }

    if (fieldRef.value?.value !== p && fieldRef.value?.updateModelValue) {
      fieldRef.value.updateModelValue(p);
    }
  }

  emit('blur');
};

const onKeyDown = (e: KeyboardEvent) => {
  if (e.key.match(/^[\d.]$/)) {
    return;
  }
  if (e.key.length > 1) {
    return;
  }

  e.preventDefault();
};

const storeValue = computed(() => formsStore.fieldGetValue(formId, props.inputId));

const displayValue = computed(() => {
  if (props.hideEmptyInput && (typeof storeValue.value === 'undefined' || storeValue.value === null)) {
    return '';
  }
  const price = isNaN(fieldRef.value?.value) ? 0 : fieldRef.value?.value;
  return formatCurrency(price, { onZero: '-' });
});

defineExpose({
  inputRef,
});
</script>
